import { useState } from 'react';
import { Calculator, DollarSign, FileText, Wrench } from 'lucide-react';
import SEO from '../components/SEO';

// Tabela de preços para Ferramentas Gerais
const generalToolsPricing = [
  { maxPieces: 200, pricePerPiece: 3.60, fixedTotal: 720 },
  { maxPieces: 300, pricePerPiece: 3.50, fixedTotal: 1050 },
  { maxPieces: 500, pricePerPiece: 3.40, fixedTotal: 1700 },
  { maxPieces: 1000, pricePerPiece: 3.30, fixedTotal: 3300 },
  { maxPieces: Infinity, pricePerPiece: 3.20, fixedTotal: null }
];

// Tabela de preços recorrentes para Ferramentas Personalizadas
const customToolsRecurringPricing = [
  { maxPages: 20, monthlyPrice: 0.38, annualPrice: 0.36 },
  { maxPages: 50, monthlyPrice: 0.37, annualPrice: 0.35 },
  { maxPages: 80, monthlyPrice: 0.36, annualPrice: 0.34 },
  { maxPages: 200, monthlyPrice: 0.35, annualPrice: 0.33 },
  { maxPages: 500, monthlyPrice: 0.33, annualPrice: 0.31 },
  { maxPages: 1000, monthlyPrice: 0.31, annualPrice: 0.29 },
  { maxPages: Infinity, monthlyPrice: 0.29, annualPrice: 0.27 }
];

// Tabela de preços únicos para implementação de Ferramentas Personalizadas
const customToolsOneTimePricing = [
  { tools: 1, totalPrice: 3000, pricePerTool: 3000 },
  { tools: 3, totalPrice: 8400, pricePerTool: 2800 },
  { tools: 5, totalPrice: 13000, pricePerTool: 2600 },
  { tools: 7, totalPrice: 16800, pricePerTool: 2400 }
];

// Preço por peça para quantidades acima de 7
const pricePerToolAbove7 = 2400;

export default function Calc() {
  // Estados para Ferramentas Gerais
  const [generalPieces, setGeneralPieces] = useState<number>(0);
  const [generalPages, setGeneralPages] = useState<number>(0);

  // Estados para Ferramentas Personalizadas
  const [customPieces, setCustomPieces] = useState<number>(0);
  const [customPages, setCustomPages] = useState<number>(0);
  const [customToolsCount, setCustomToolsCount] = useState<number>(1);
  // Limite de páginas para cálculo de pagamento único (30, 50, 80, 100)
  const [oneTimeMaxPages, setOneTimeMaxPages] = useState<number>(30);

  const getOneTimeMultiplier = (maxPages: number): number => {
    if (maxPages <= 30) return 1;
    if (maxPages <= 50) return 1.2;
    if (maxPages <= 80) return 1.5;
    if (maxPages <= 100) return 1.75;
    return 1.75;
  };
  const oneTimeMultiplier = getOneTimeMultiplier(oneTimeMaxPages);

  // Função para calcular preço das Ferramentas Gerais
  const getGeneralToolsPrice = (pieces: number) => {
    const tier = generalToolsPricing.find(tier => pieces <= tier.maxPieces);
    if (!tier) return { pricePerPiece: 3.20, total: pieces * 3.20 };
    
    // Se tem valor fixo (até 1000 peças), usa o valor fixo
    if (tier.fixedTotal !== null) {
      return { pricePerPiece: tier.pricePerPiece, total: tier.fixedTotal };
    }
    
    // Acima de 1000 peças, multiplica o preço por peça
    return { pricePerPiece: tier.pricePerPiece, total: pieces * tier.pricePerPiece };
  };

  // Função para calcular preço por página baseado na quantidade
  const getPricePerPage = (pages: number, isAnnual: boolean = false) => {
    const tier = customToolsRecurringPricing.find(tier => pages <= tier.maxPages);
    if (!tier) return isAnnual ? 0.27 : 0.29;
    return isAnnual ? tier.annualPrice : tier.monthlyPrice;
  };

  // Função para calcular preço único de implementação
  const getOneTimePrice = (toolsCount: number) => {
    // Se for até 7 ferramentas, usa valores fixos da tabela
    const tier = customToolsOneTimePricing.find(tier => toolsCount === tier.tools);
    if (tier) {
      return { total: tier.totalPrice * oneTimeMultiplier, perTool: tier.pricePerTool * oneTimeMultiplier };
    }
    
    // Se for acima de 7, multiplica pelo preço unitário
    if (toolsCount > 7) {
      return { total: toolsCount * pricePerToolAbove7 * oneTimeMultiplier, perTool: pricePerToolAbove7 * oneTimeMultiplier };
    }
    
    // Para valores entre as faixas (2, 4, 6), retorna null para não mostrar cálculo
    return null;
  };

  // Cálculos para Ferramentas Gerais
  const generalPricing = getGeneralToolsPrice(generalPieces);
  const generalTotalMonthly = generalPricing.total;
  const generalTotalAnnual = generalTotalMonthly * 12;

  // Cálculos para Ferramentas Personalizadas (Recorrente)
  const customPricePerPageMonthly = getPricePerPage(customPages, false);
  const customPricePerPageAnnual = getPricePerPage(customPages, true);
  const customTotalPagesPerMonth = customPieces * customPages;
  const customTotalMonthly = customTotalPagesPerMonth * customPricePerPageMonthly;
  const customTotalAnnual = customTotalMonthly * 12;

  // Cálculos para Ferramentas Personalizadas (Pagamento Único)
  const oneTimePrice = getOneTimePrice(customToolsCount);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#081428] via-[#0A1B3A] to-[#081428] text-white">
      <SEO
        title="Calculadora de Preços"
        description="Calcule o preço das ferramentas de IA para advogados da LawX. Ferramentas gerais e personalizadas com preços transparentes."
        keywords="calculadora de preços, preços LawX, ferramentas jurídicas, preços IA para advogados"
        url="/calc"
      />
      {/* Header */}
      <div className="container mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-[#28E7C5] to-[#6942E2] bg-clip-text text-transparent">
            Calculadora de Preços
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Calcule os custos das nossas ferramentas baseado no seu volume de uso
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Ferramentas Gerais */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <Wrench className="w-8 h-8 text-[#28E7C5] mr-3" />
              <h2 className="text-2xl font-bold">Ferramentas Gerais</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Quantidade total de peças por mês
                </label>
                <input
                  type="number"
                  value={generalPieces}
                  onChange={(e) => setGeneralPieces(Number(e.target.value))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#28E7C5] focus:border-transparent"
                  placeholder="Ex: 500"
                />
              </div>


              {generalPieces > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4 text-[#28E7C5]">Resultado</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Preço por peça:</span>
                      <span className="font-semibold">{formatCurrency(generalPricing.pricePerPiece)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Quantidade de peças:</span>
                      <span className="font-semibold">{generalPieces} peças</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total mensal:</span>
                      <span className="font-semibold text-[#28E7C5]">{formatCurrency(generalTotalMonthly)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Total anual:</span>
                      <span className="font-semibold text-[#28E7C5]">{formatCurrency(generalTotalAnnual)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Ferramentas Personalizadas */}
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8">
            <div className="flex items-center mb-6">
              <Calculator className="w-8 h-8 text-[#6942E2] mr-3" />
              <h2 className="text-2xl font-bold">Ferramentas Personalizadas</h2>
            </div>
            
            <div className="space-y-6">
              {/* Recorrente */}
              <div className="border-b border-white/10 pb-6">
                <h3 className="text-lg font-semibold mb-4 text-[#6942E2]">Preço Recorrente</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Quantidade total de peças por mês
                    </label>
                    <input
                      type="number"
                      value={customPieces}
                      onChange={(e) => setCustomPieces(Number(e.target.value))}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6942E2] focus:border-transparent"
                      placeholder="Ex: 100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Quantidade média de páginas por peça
                    </label>
                    <input
                      type="number"
                      value={customPages}
                      onChange={(e) => setCustomPages(Number(e.target.value))}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6942E2] focus:border-transparent"
                      placeholder="Ex: 10"
                    />
                  </div>

                  {customPieces > 0 && customPages > 0 && (
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-300">Preço por página (mensal):</span>
                          <span className="font-semibold">{formatCurrency(customPricePerPageMonthly)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Preço por página (anual):</span>
                          <span className="font-semibold">{formatCurrency(customPricePerPageAnnual)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Total de páginas/mês:</span>
                          <span className="font-semibold">{customTotalPagesPerMonth} páginas</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Total mensal:</span>
                          <span className="font-semibold text-[#6942E2]">{formatCurrency(customTotalMonthly)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-300">Total anual:</span>
                          <span className="font-semibold text-[#6942E2]">{formatCurrency(customTotalAnnual)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Pagamento Único */}
              <div>
                <h3 className="text-lg font-semibold mb-4 text-[#6942E2]">Pagamento Único (Implementação)</h3>
                
                {/* Seletor de páginas para multiplicador (pílulas) */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Até X páginas por ferramenta
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[30, 50, 80, 100].map((value) => {
                      const isActive = oneTimeMaxPages === value;
                      const label =
                        value === 30
                          ? 'Até 30 páginas\n(preços base)'
                          : value === 50
                          ? 'Até 50 páginas\n(+20%)'
                          : value === 80
                          ? 'Até 80 páginas\n(+50%)'
                          : 'Até 100 páginas\n(+75%)';
                      return (
                        <button
                          key={value}
                          type="button"
                          aria-pressed={isActive}
                          onClick={() => setOneTimeMaxPages(value)}
                          className={
                            (isActive
                              ? 'bg-gradient-to-r from-[#28E7C5] to-[#6942E2] text-white '
                              : 'bg-white/10 text-gray-200 hover:bg-white/15 ') +
                            'whitespace-pre-line px-4 py-3 rounded-lg border border-white/20 transition-colors'
                          }
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs text-gray-400">Multiplicadores: base, +20%, +50%, +75%.</p>
                </div>
                
                {/* Tabela de preços fixos */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-300 mb-3">Preços por quantidade:</h4>
                  <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                    <div className="grid grid-cols-3 gap-4 p-4 text-sm">
                      <div className="font-semibold text-[#6942E2]">Quantidade</div>
                      <div className="font-semibold text-[#6942E2]">Preço por ferramenta</div>
                      <div className="font-semibold text-[#6942E2]">Total</div>
                      
                      <div>1 ferramenta</div>
                      <div>{formatCurrency(3000 * oneTimeMultiplier)}</div>
                      <div>{formatCurrency(3000 * oneTimeMultiplier)}</div>
                      
                      <div>3 ferramentas</div>
                      <div>{formatCurrency(2800 * oneTimeMultiplier)}</div>
                      <div>{formatCurrency(8400 * oneTimeMultiplier)}</div>
                      
                      <div>5 ferramentas</div>
                      <div>{formatCurrency(2600 * oneTimeMultiplier)}</div>
                      <div>{formatCurrency(13000 * oneTimeMultiplier)}</div>
                      
                      <div>7 ferramentas</div>
                      <div>{formatCurrency(2400 * oneTimeMultiplier)}</div>
                      <div>{formatCurrency(16800 * oneTimeMultiplier)}</div>
                    </div>
                  </div>
                </div>

                {/* Calculadora para acima de 7 */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Para quantidades acima de 7 ferramentas:
                  </label>
                  <input
                    type="number"
                    value={customToolsCount}
                    onChange={(e) => setCustomToolsCount(Number(e.target.value))}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#6942E2] focus:border-transparent"
                    placeholder="Ex: 10"
                    min="8"
                  />
                </div>

                {customToolsCount > 7 && oneTimePrice && (
                  <div className="bg-white/5 border border-white/10 rounded-lg p-4 mt-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Quantidade de ferramentas:</span>
                        <span className="font-semibold">{customToolsCount} ferramentas</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Preço por ferramenta:</span>
                        <span className="font-semibold">{formatCurrency(oneTimePrice.perTool)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Total:</span>
                        <span className="font-semibold text-[#6942E2]">{formatCurrency(oneTimePrice.total)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12">
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Pronto para começar?</h3>
            <p className="text-gray-300 mb-6">
              Entre em contato conosco para implementar a solução ideal para seu escritório
            </p>
            <a
              href="https://api.whatsapp.com/send?phone=5584999869794&text=Ol%C3%A1!%20Quero%20saber%20mais%20sobre%20os%20pre%C3%A7os%20das%20ferramentas!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#28E7C5] to-[#6942E2] text-white rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(40,231,197,0.4)] hover:scale-105"
            >
              <DollarSign className="w-5 h-5 mr-2" />
              Falar com Especialista
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
